import { useState, useEffect } from 'react';
import { useAppSelector } from '../redux/reduxHooks';
import api, { backendUrl } from '../constants/urls';

const TRANSACTION_TYPES = ['remit', 'power', 'topup', 'withdrawal', 'refund', 'paybill', 'banking', 'event', 'wallet_transaction', 'agent_transaction', 'product', 'kalpeh', 'ticketTransfer', 'airtime', 'cashback'];

export interface SearchResult {
	id: string;
	type: 'customer' | 'merchant' | 'normal' | 'master' | 'remit' | 'power' | 'topup' | 'withdrawal' | 'refund' | 'paybill' | 'banking' | 'event' | 'wallet_transaction' | 'agent_transaction' | 'product' | 'kalpeh' | 'ticketTransfer' | 'airtime' | 'cashback';
	title: string;
	subtitle: string;
	reference?: string;
	phone?: string;
	status?: string;
	accountNumber?: string;
	businessName?: string;
	email?: string;
}



const transformSearchResult = (item: any): SearchResult => {
	let title = '';
	let subtitle = '';
	let phone = '';
	let accountNumber = '';
	let businessName = '';
	let email = '';

	if (item.type === 'customer') {
		title = item.fullname || `${item.firstname || ''} ${item.lastname || ''}`.trim() || 'Unknown Customer';
		subtitle = `Account: ${item.customerAccountNumber || item.accountNumber || 'N/A'}`;
		phone = item.mobileNumber || '';
		accountNumber = item.customerAccountNumber || item.accountNumber || '';
		email = item.email || '';
	} else if (item.type === 'merchant') {
		title = item.businessName || `${item.firstname || ''} ${item.lastname || ''}`.trim() || 'Unknown Merchant';
		subtitle = `Merchant Account: ${item.accountNumber || 'N/A'}`;
		phone = item.mobileNumber || '';
		accountNumber = item.accountNumber || '';
		businessName = item.businessName || '';
		email = item.email || '';
	} else if (item.type === 'normal' || item.type === 'master') {
		title = `${item.firstname || ''} ${item.middlename || ''} ${item.lastname || ''}`.trim() || 'Unknown Agent';
		subtitle = item.businessName || `${item.type === 'master' ? 'Master' : 'Normal'} Agent - Account: ${item.accountNumber || 'N/A'}`;
		phone = item.mobileNumber || '';
		accountNumber = item.accountNumber || '';
		businessName = item.businessName || '';
		email = item.email || '';
	} else if (TRANSACTION_TYPES.includes(item.type)) {
		title = item.referenceId || 'Unknown Transaction';
		subtitle = `${item.type || 'Transaction'} - ${item.amount || 'N/A'}`;
		phone = item.customerPhone || '';
	}

	return {
		id: item.id,
		type: item.type,
		title,
		subtitle,
		phone,
		status: item.status,
		accountNumber,
		businessName,
		email,
	};
};

export const useGlobalSearch = () => {
	const [isLoading, setIsLoading] = useState(false);
	const { user } = useAppSelector((state) => state.auth);

	const performSearch = async (query: string): Promise<SearchResult[]> => {
		if (!query.trim()) {
			return [];
		}

		setIsLoading(true);

		try {
			const response = await api.get(`${backendUrl}/admin/global-search`, {
				params: { query },
				headers: {
					Authorization: `Bearer ${user?.token}`,
				},
			});

			if (response.data.success && response.data.data) {
				const transformedResults = response.data.data.map(transformSearchResult);
				return transformedResults;
			}

			return [];
		} catch (error) {
			console.error('Global search error:', error);
			return [];
		} finally {
			setIsLoading(false);
		}
	};

	return {
		performSearch,
		isLoading
	};
};

export const searchService = {
	async searchAll(query: string, userToken?: string): Promise<SearchResult[]> {
		if (!query.trim()) {
			return [];
		}

		try {
			const response = await api.get(`${backendUrl}/admin/global-search`, {
				params: { query },
				headers: {
					Authorization: `Bearer ${userToken}`,
				},
			});

			if (response.data.success && response.data.data) {
				return response.data.data.map(transformSearchResult);
			}

			return [];
		} catch (error) {
			console.error('Search error:', error);
			return [];
		}
	},

	async searchTransactions(query: string, userToken?: string): Promise<SearchResult[]> {
		const results = await this.searchAll(query, userToken);
		return results.filter(item => TRANSACTION_TYPES.includes(item.type));
	},

	async searchCustomers(query: string, userToken?: string): Promise<SearchResult[]> {
		const results = await this.searchAll(query, userToken);
		return results.filter(item => item.type === 'customer');
	},

	async searchAgents(query: string, userToken?: string): Promise<SearchResult[]> {
		const results = await this.searchAll(query, userToken);
		return results.filter(item => item.type === 'normal' || item.type === 'master');
	},

	async searchMerchants(query: string, userToken?: string): Promise<SearchResult[]> {
		const results = await this.searchAll(query, userToken);
		return results.filter(item => item.type === 'merchant');
	}
};
